import {usePortfolioData} from "../../data";
import {Project, ProjectTypes} from "../../../../data/model/project";
import {Tooltip, TooltipContent, TooltipTrigger} from "../../../../components/ui/tooltip";
import { Badge } from "../../../../components/ui/badge";
import {Link} from "react-router-dom";
import {Button} from "../../../../components/ui/button";
import {
    AppWindowIcon, Code2Icon,
    ExternalLinkIcon,
    LucideProps,
    MonitorIcon,
    SmartphoneIcon
} from "lucide-react";
import {cn} from "../../../../lib/utils";
import {HTMLProps} from "react";
import {OptionalLink} from "../../components/optional-link";
import {MediaCarousel} from "../../components/media-carousel";
import {ItemsList} from "../../components/item-list";
import {ListItem} from "../../../../components/list-item";
import {SimpleLogo} from "../../components/simple-logo";

export function ProjectsSubpage () {
    const { projects } = usePortfolioData();
    return <ItemsList className={"animate-in fade-in duration-300 slide-in-from-bottom-2"}>
        {projects.map((project, index) => {
            return <ProjectItem key={index} project={project}/>
        })}
    </ItemsList>
}

function ProjectItem({project}: { project: Project }) {
    return <div className={"p-4"}>
        <ListItem>
            <ListItem.Logo>
                <ProjectIcon project={project} className={"w-16 self-start shrink-0"} />
            </ListItem.Logo>
            <ListItem.Header>
                <ProjectHeader project={project} />
            </ListItem.Header>
            <ListItem.Description>
                <div className={"flex flex-col gap-3 grow"}>
                    <div className={"text-muted-foreground whitespace-pre-wrap"}>{project.description}</div>
                    {project.highlight && <div
                        className={"flex flex-row items-center gap-4 px-4 py-1 border border-secondary/20 bg-secondary/5 rounded-md"}>
                        <div>✨</div>
                        <div className={"text-sm opacity-65 font-medium"}>{project.highlight}</div>
                    </div>}
                    {project.technologies && <ProjectTech technologies={project.technologies}/>}
                    <MediaCarousel media={project.media}/>
                </div>
            </ListItem.Description>
        </ListItem>
    </div>
}

function ProjectTech(props: { technologies: string[] }) {
    return <div className={"flex flex-row items-stretch gap-2"}>
        <div className={"shrink-0"}>
            <Code2Icon className={"w-5 h-5 shrink-0 opacity-30"}/>
        </div>
        <div className={"flex flex-row items-center"}>
            <div className={"flex flex-row flex-wrap gap-x-3 gap-y-1 text-xs text-foreground/30 font-medium"}>
                {props.technologies.map((tech, index) => (
                    <div key={tech}
                         className={"transition-all hover:text-foreground/45 cursor-default duration-500"}>{tech}</div>
                ))}
            </div>
        </div>
    </div>
}

function ProjectIcon({project, ...props}: { project: Project } & HTMLProps<HTMLDivElement>) {
    return <div {...props}>
        {project.logo && (
            <SimpleLogo
                className={cn("w-16 aspect-square rounded-xl transition-all duration-500", project.link && "hover:scale-105")}
                logo={project.logo} alt={`${project.title} Logo`}
            />
        )}
        { !project.logo && (
            <div
                className={"w-16 aspect-square bg-foreground/5 rounded-md flex flex-col items-center justify-center opacity-45"}>
                <IconForProjectType type={project.type}/>
            </div>
        )}
    </div>
}

function IconForProjectType(props: { type: ProjectTypes } & LucideProps) {
    if (props.type === ProjectTypes.MobileApp) {
        return <SmartphoneIcon {...props} />
    } else if (props.type === ProjectTypes.WebApp) {
        return <MonitorIcon {...props} />
    } else if (props.type === ProjectTypes.Website) {
        return <AppWindowIcon {...props} />
    }

    return null;
}

function ProjectHeader({project}: { project: Project }) {
    const {birthYear} = usePortfolioData();
    return <div className={"flex flex-row flex-wrap gap-3 items-center justify-between w-full"}>
        <div className={"flex flex-col gap-1"}>
            <div className={"flex flex-row items-center"}>
                <h3 className={"flex flex-row items-center"}>{project.title}</h3>
                <span className={"text-muted-foreground mx-2 font-medium"}>-</span>
                <Tooltip delayDuration={200}>
                    <TooltipTrigger>
                        <p className={"text-muted-foreground cursor-default font-medium"}>{project.year}</p>
                    </TooltipTrigger>
                    <TooltipContent>
                        I was {project.year - birthYear} years old when I released {project.title}.
                    </TooltipContent>
                </Tooltip>
            </div>
            <div className={"flex flex-row items-center gap-2"}>
                <Badge variant={"outline"} className={"py-1"}>
                    <IconForProjectType type={project.type} className={"w-4 h-4 me-1.5"}/>
                    {project.type}
                </Badge>

                {project.discontinued && (
                    <Badge variant={"outline-destructive"}>
                        discontinued
                    </Badge>
                )}
            </div>
        </div>
        {project.link && (
            <Link to={project.link} target={"_blank"} className={"self-start"}>
                <Button size={"sm"}
                        variant={"outline-primary"}>{project.cta ?? `Open ${project.title}`}<ExternalLinkIcon
                    className={"opacity-65"}/></Button>
            </Link>
        )}
    </div>
}